/**
 * Vertical slice tuning data.
 *
 * This file intentionally contains only one polished run profile.
 */

import { GateLane, Lane } from './LevelData';

export type SliceLane = Lane;

export interface BarrierConfig {
  triggerDistance: number;
  lane: SliceLane;
  baseHp: number;
  rewardSoldiers: number;
  rewardScore: number;
  width?: number;
}

export interface SliceEnemyConfig {
  triggerDistance: number;
  type: 'basic' | 'elite';
  lane: SliceLane;
  hp?: number;
  spawnY?: number;
}

export interface SliceGateOption {
  lane: GateLane;
  multiplier: 2 | 3 | 5;
}

export interface SliceGateRowConfig {
  triggerDistance: number;
  options: SliceGateOption[];
}

export interface SliceBeatConfig {
  id: string;
  triggerDistance: number;
  barriers: BarrierConfig[];
  enemies: SliceEnemyConfig[];
  gates: SliceGateRowConfig[];
}

export interface SliceLevelConfig {
  id: 'slice-1';
  trackLength: number;
  finaleDistance: number;
  targetDurationSeconds: number;
  beats: SliceBeatConfig[];
}

const VERTICAL_SLICE_LEVEL: SliceLevelConfig = {
  id: 'slice-1',
  // ~44s at 95px/s
  trackLength: 4200,
  finaleDistance: 3600,
  targetDurationSeconds: 45,
  beats: [
    {
      id: 'beat-1-open',
      triggerDistance: 80,
      barriers: [
        {
          triggerDistance: 180,
          lane: 'center',
          baseHp: 10,
          rewardSoldiers: 1,
          rewardScore: 90,
        },
      ],
      enemies: [
        { triggerDistance: 20, type: 'basic', lane: 'left', spawnY: 120 },
        { triggerDistance: 120, type: 'basic', lane: 'right', spawnY: 70 },
        { triggerDistance: 260, type: 'basic', lane: 'center' },
      ],
      gates: [
        {
          triggerDistance: 540,
          options: [
            { lane: 'left', multiplier: 2 },
            { lane: 'right', multiplier: 3 },
          ],
        },
      ],
    },
    {
      id: 'beat-2-gate-choice',
      triggerDistance: 980,
      barriers: [
        {
          triggerDistance: 980,
          lane: 'left',
          baseHp: 18,
          rewardSoldiers: 2,
          rewardScore: 120,
        },
      ],
      enemies: [
        { triggerDistance: 860, type: 'basic', lane: 'center' },
        { triggerDistance: 1010, type: 'basic', lane: 'right' },
        { triggerDistance: 1120, type: 'basic', lane: 'left' },
      ],
      gates: [
        {
          triggerDistance: 1260,
          options: [
            { lane: 'left', multiplier: 3 },
            { lane: 'right', multiplier: 2 },
          ],
        },
      ],
    },
    {
      id: 'beat-3-pressure',
      triggerDistance: 1640,
      barriers: [
        {
          triggerDistance: 1640,
          lane: 'right',
          baseHp: 24,
          rewardSoldiers: 2,
          rewardScore: 160,
        },
        {
          triggerDistance: 1860,
          lane: 'center',
          baseHp: 22,
          rewardSoldiers: 2,
          rewardScore: 160,
        },
      ],
      enemies: [
        { triggerDistance: 1540, type: 'basic', lane: 'left' },
        { triggerDistance: 1700, type: 'basic', lane: 'right' },
        { triggerDistance: 1880, type: 'basic', lane: 'center' },
      ],
      gates: [
        {
          triggerDistance: 2060,
          options: [
            { lane: 'left', multiplier: 2 },
            { lane: 'right', multiplier: 5 },
          ],
        },
      ],
    },
    {
      id: 'beat-4-escalation',
      triggerDistance: 2460,
      barriers: [
        {
          triggerDistance: 2460,
          lane: 'left',
          baseHp: 28,
          rewardSoldiers: 2,
          rewardScore: 180,
        },
        {
          triggerDistance: 2680,
          lane: 'right',
          baseHp: 30,
          rewardSoldiers: 2,
          rewardScore: 200,
        },
      ],
      enemies: [
        { triggerDistance: 2360, type: 'basic', lane: 'center' },
        { triggerDistance: 2520, type: 'basic', lane: 'left' },
        { triggerDistance: 2670, type: 'elite', lane: 'right', hp: 7 },
      ],
      gates: [
        {
          triggerDistance: 2860,
          options: [
            { lane: 'left', multiplier: 3 },
            { lane: 'right', multiplier: 5 },
          ],
        },
      ],
    },
    {
      id: 'beat-5-finale-approach',
      triggerDistance: 3200,
      barriers: [
        {
          triggerDistance: 3200,
          lane: 'center',
          baseHp: 32,
          rewardSoldiers: 2,
          rewardScore: 220,
        },
      ],
      enemies: [
        { triggerDistance: 3080, type: 'basic', lane: 'left' },
        { triggerDistance: 3160, type: 'basic', lane: 'center' },
        { triggerDistance: 3240, type: 'basic', lane: 'right' },
        { triggerDistance: 3320, type: 'elite', lane: 'center', hp: 8 },
      ],
      gates: [],
    },
  ],
};

export function getVerticalSliceConfig(): SliceLevelConfig {
  return VERTICAL_SLICE_LEVEL;
}
